import Delete from '@mui/icons-material/Delete'
import Merge from '@mui/icons-material/Merge'
import Warning from '@mui/icons-material/Warning'
import { LoadingButton } from '@mui/lab'
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  Divider,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { Project } from '@/types/projects'
import { RollupConfig, RollupConfigVendor } from '@/types/rollup'
import {
  rollupConfigDraftLabel,
  rollupConfigSubtitle,
  rollupConfigTitle,
} from '@/utils/rollup-config-grouping'
import { RollupKeyDependencyMap } from '@/utils/rollup-key-dependency'
import AddVendorDialog from './AddVendorDialog'
import DeleteRollupConfigDialog from './DeleteRollupConfigDialog'
import EmptyKeyConfirmDialog from './EmptyKeyConfirmDialog'
import RollupConfigVendors from './RollupConfigVendors'
import RollupLiveExample from './RollupLiveExample'
import {
  disabledPillSx,
  draftPillDotSx,
  draftPillSx,
  unsavedCardBorderColor,
} from './rollupWarningStyles'
import { RollupKeyField } from './useRollupKeyFields'

const LOSS_ITEMS = [
  'Quantity will not be exported to your ERP',
  'Rate will not be exported to your ERP',
]

type RollupConfigCardProps = {
  config: RollupConfig
  companyName: string
  project: Project
  isDraft: boolean
  isDirty: boolean
  isVendorRequired: boolean
  /** A save for this card is in flight. Save, Discard and the enable toggle
   * are locked — discarding can't recall the request already on its way, and
   * the toggle would race it with a competing write. Delete stays live on
   * purpose; see the note on it below. */
  isSaving: boolean
  keyFields: RollupKeyField[]
  /** Direct required-ancestor edges (not transitive) — used only to decide
   * whether adjacent pills get an arrow (a real dependency) or a plain
   * divider (independent dimensions that just happen to sit next to each
   * other in the project's field order). The toggle-cascade logic that
   * actually enforces ancestor-completeness lives in the caller. */
  requiredAncestorsByInfoTypeId: RollupKeyDependencyMap
  /** Whether the live example section starts expanded on this card. */
  defaultLiveExampleOpen: boolean
  excludedVendorNumbers: Set<string>
  claimedVendorNumbers: Set<string>
  onToggleEnabled: () => void
  onAddVendor: (vendor: RollupConfigVendor) => void
  onRemoveVendor: (vendorNum: string) => void
  onToggleKeyInfoType: (infoTypeId: string) => void
  onSave: () => void
  onDiscard: () => void
  onDelete: () => void
}

export default function RollupConfigCard({
  config,
  companyName,
  project,
  isDraft,
  isDirty,
  isVendorRequired,
  isSaving,
  keyFields,
  requiredAncestorsByInfoTypeId,
  defaultLiveExampleOpen,
  excludedVendorNumbers,
  claimedVendorNumbers,
  onToggleEnabled,
  onAddVendor,
  onRemoveVendor,
  onToggleKeyInfoType,
  onSave,
  onDiscard,
  onDelete,
}: RollupConfigCardProps) {
  const addVendorOverlay = useOverlay()
  const deleteOverlay = useOverlay()
  const emptyKeyConfirmOverlay = useOverlay()

  const title = rollupConfigTitle(config, isVendorRequired)
  const subtitle = rollupConfigSubtitle(config, isVendorRequired)
  const draftLabel = rollupConfigDraftLabel({ isDraft, isDirty })

  const keptFields = keyFields.filter((field) =>
    config.key_info_types.includes(field.infoTypeId),
  )
  const keptFieldIds = new Set(keptFields.map((field) => field.infoTypeId))
  const keptNamesText =
    keptFields.length > 0
      ? keptFields.map((field) => field.label).join(' · ')
      : '(nothing — all lines merge into one)'
  // Quantity/rate are nulled on any merged line, so leaving even one
  // eligible field out of the key risks losing them on export.
  const isEveryFieldKept =
    keyFields.length > 0 && keptFieldIds.size === keyFields.length

  const currentVendorNums = new Set(
    config.vendors.map((vendor) => vendor.vendor_num),
  )

  // An unsaved draft is discarded outright — there's nothing on the server
  // to confirm deleting.
  const handleDeleteClick = () => {
    if (isDraft) {
      onDelete()
    } else {
      deleteOverlay.open()
    }
  }

  // An empty grouping key merges every line item on export — allowed, but
  // only behind an explicit confirm (epic Resolved Decisions).
  const handleSaveClick = () => {
    if (keptFieldIds.size === 0) {
      emptyKeyConfirmOverlay.open()
    } else {
      onSave()
    }
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: isDraft || isDirty ? unsavedCardBorderColor : undefined,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 34,
              height: 34,
              flex: 'none',
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}
          >
            <Merge sx={{ fontSize: 17, transform: 'rotate(90deg)' }} />
          </Stack>
          <Stack>
            <Typography sx={{ fontWeight: 500 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Independent of one another: a disabled config can also have
              unsaved edits, and the card should say so. */}
          {draftLabel && (
            <Chip
              size="small"
              label={draftLabel}
              icon={<Box sx={draftPillDotSx} />}
              sx={draftPillSx}
            />
          )}
          {!config.is_enabled && (
            <Chip size="small" label="Disabled" sx={disabledPillSx} />
          )}
          <Switch
            checked={config.is_enabled}
            disabled={isSaving}
            onChange={onToggleEnabled}
            inputProps={{ 'aria-label': `${companyName} — ${title} enabled` }}
          />
          {/* Deliberately NOT gated on isSaving. The axios client has no
              timeout, so a hung PATCH would otherwise leave every control on
              this card disabled with no way out but a page reload. Deleting
              mid-save can make the in-flight PATCH 404 into an error
              snackbar, which is a better outcome than a stuck card. */}
          <IconButton
            aria-label={`Delete ${companyName} — ${title} config`}
            onClick={handleDeleteClick}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Divider />

      <Box
        sx={{
          px: 2,
          py: 2,
          opacity: config.is_enabled ? 1 : 0.45,
          pointerEvents: config.is_enabled ? 'auto' : 'none',
        }}
      >
        <Stack spacing={2}>
          {config.dp_org_id !== null && (
            <>
              <RollupConfigVendors
                vendors={config.vendors}
                isVendorRequired={isVendorRequired}
                isDisabled={!config.is_enabled}
                companyName={companyName}
                configLabel={title}
                excludedVendorNumbers={excludedVendorNumbers}
                onRemoveVendor={onRemoveVendor}
                onAddVendorClick={addVendorOverlay.open}
              />
              <Divider />
            </>
          )}

          <Stack spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            >
              Grouping key
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              {keyFields.map((field, index) => {
                const isKept = keptFieldIds.has(field.infoTypeId)
                const previousField = keyFields[index - 1]
                // A real dependency edge draws an arrow (this field requires
                // the one immediately before it in the project's field
                // order); anything else — independent dimensions that just
                // happen to be adjacent — gets a plain divider instead, so
                // the arrow is never shown where it wouldn't be true.
                const isChainedToPrevious =
                  !!previousField &&
                  !!requiredAncestorsByInfoTypeId
                    .get(field.infoTypeId)
                    ?.has(previousField.infoTypeId)
                return (
                  <Stack
                    key={field.infoTypeId}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                  >
                    {index > 0 &&
                      (isChainedToPrevious ? (
                        <Typography aria-hidden="true" color="text.disabled">
                          &rarr;
                        </Typography>
                      ) : (
                        <Box
                          aria-hidden="true"
                          sx={{
                            width: '1px',
                            height: 20,
                            bgcolor: 'divider',
                          }}
                        />
                      ))}
                    <ButtonBase
                      disabled={!config.is_enabled}
                      onClick={() => onToggleKeyInfoType(field.infoTypeId)}
                      aria-pressed={isKept}
                      aria-label={`${field.label} — ${
                        isKept ? 'kept in' : 'not in'
                      } the grouping key`}
                      sx={{
                        borderRadius: 2,
                        '&:focus-visible': { outline: '2px solid' },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          px: 1.75,
                          py: 0.75,
                          borderRadius: 2,
                          border: (theme) =>
                            isKept
                              ? `1.5px solid ${theme.palette.primary.main}`
                              : `1px dashed ${theme.palette.divider}`,
                          bgcolor: (theme) =>
                            isKept
                              ? alpha(theme.palette.primary.main, 0.12)
                              : 'transparent',
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: isKept ? 'primary.main' : 'text.disabled',
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500 }}
                          color={isKept ? 'text.primary' : 'text.disabled'}
                        >
                          {field.label}
                        </Typography>
                      </Stack>
                    </ButtonBase>
                  </Stack>
                )
              })}
            </Stack>
            <Stack direction="row" spacing={2}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Kept in grouping key
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'text.disabled',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Rolled up · not used in the grouping key
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Lines with a matching{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 700 }}
              >
                {keptNamesText}
              </Typography>{' '}
              combination are grouped into one. Amounts are summed; fields
              outside the key still export, taking their value from the line
              with the largest amount within the group. Your line items stay
              intact in Pixydocs — rollup happens only on export, and it
              can&apos;t be reversed downstream.
            </Typography>
          </Stack>

          <RollupLiveExample
            keyFields={keyFields}
            keptFieldIds={keptFieldIds}
            defaultOpen={defaultLiveExampleOpen}
          />

          {!isEveryFieldKept && (
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rolling up line items means some data will be lost:
              </Typography>
              <Stack spacing={0.5}>
                {LOSS_ITEMS.map((lossText) => (
                  <Stack
                    key={lossText}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Warning
                      aria-hidden="true"
                      sx={{ fontSize: 14, color: 'warning.main' }}
                    />
                    <Typography variant="body2">{lossText}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Box>

      {(isDraft || isDirty) && (
        <>
          <Divider />
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ px: 2, py: 1.5 }}
          >
            <Button variant="text" onClick={onDiscard} disabled={isSaving}>
              {isDraft ? 'Discard draft' : 'Discard'}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleSaveClick}
              loading={isSaving}
              disabled={isVendorRequired}
            >
              Save config
            </LoadingButton>
          </Stack>
        </>
      )}

      {config.dp_org_id !== null && (
        <AddVendorDialog
          overlay={addVendorOverlay}
          project={project}
          companyId={config.dp_org_id}
          companyName={companyName}
          currentVendorNums={currentVendorNums}
          claimedVendorNums={claimedVendorNumbers}
          onAdd={onAddVendor}
        />
      )}

      {!isDraft && (
        <DeleteRollupConfigDialog
          overlay={deleteOverlay}
          configLabel={title}
          companyName={companyName}
          onDelete={onDelete}
        />
      )}

      <EmptyKeyConfirmDialog
        overlay={emptyKeyConfirmOverlay}
        onConfirm={onSave}
      />
    </Card>
  )
}
